/**
 * Logic Description Service
 * 
 * Simplified version of the webapp's logic describer that works with prop data
 * instead of stores. Used to generate human-readable condition descriptions.
 */

// Helper function to handle time unit pluralization
const formatDuration = (duration: number, unit: string) => {
  // For singular (1), remove 's' if present
  if (duration === 1) {
    return unit.endsWith('s') ? unit.slice(0, -1) : unit
  }
  // For plural, ensure unit ends with 's'
  return unit.endsWith('s') ? unit : unit + 's'
}

// Helper function to singularize a word (basic implementation)
const singularize = (word: string) => {
  if (word.endsWith('ies')) {
    return word.slice(0, -3) + 'y'
  }
  if (word.endsWith('es')) {
    return word.slice(0, -2)
  }
  if (word.endsWith('s')) {
    return word.slice(0, -1)
  }
  return word
}

type LogicItem = {
  $type: string
  value: string
  $source: string
  operator: string
  timeframe?: string
  occurrence?: string
  [key: string]: any // Allow for additional properties
}

type LogicObject = {
  and?: LogicItem[]
  or?: LogicItem[]
}

const stringOperatorMap: { [key: string]: string } = {
  'equals': 'is',
  'does not equal': "isn't",
  'glob': 'matches',
  'is set': 'is set',
  'is not set': 'is blank',
  'between': 'between',
  'before': 'before',
  'after': 'after',
  'include any': 'includes',
  'do not include any': "doesn't include"
}

const comparisonLabels: { [key: string]: string } = {
  'gte': 'at least',
  'gt': 'more than',
  'lte': 'at most',
  'lt': 'less than',
  'equals': 'exactly'
}

const operatorAndValue = (operator: string, value: string) => {
  if (['is set', 'is not set'].includes(operator)) {
    return stringOperatorMap[operator]
  } else {
    return `${stringOperatorMap[operator] || operator} **${value || '[]'}**`
  }
}

const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

const stripHtml = (str: string) => {
  return str.replace(/<[^>]*>/g, '')
}

const describeLogicObject = async (
  item: LogicItem,
  context: {
    preview?: boolean
    partners?: any[]
    segmentGroups?: any[]
    questions?: any[]
    widgets?: any[]
  } = {}
): Promise<string> => {
  const { preview = false, partners = [], segmentGroups = [], questions = [], widgets = [] } = context

  // Find partner if applicable
  const partner = partners.find((p: any) => p.name === item.$source)

  if (partner) {
    if (item.$type === 'isAnonymous') {
      return `Does not exist in ${partner.label || partner.name}`
    }

    if (item.$type === 'isSubscriber') {
      return `Exists in ${partner.label || partner.name}`
    }

    if (item.$type === 'tags') {
      if (preview) {
        return 'Is tagged'
      }
      return `tagged in ${partner.label || partner.name} ${item.value || '(please set)'}`
    }

    if (item.$type === 'customField') {
      const fieldName = item.customFieldId || item.value
      const customFieldLabel = (partner.espFeatureSupport?.customField?.label || 'custom field').toLowerCase()
      return `${partner.label || partner.name} ${customFieldLabel} **${fieldName || '(please set)'}** ${operatorAndValue(item.operator, item.value)}`
    }

    if (item.$type === 'lists') {
      const listLabel = singularize(partner.espFeatureSupport?.list?.label?.toLowerCase() || 'list')
      if (preview) {
        return `Belongs to a ${listLabel}`
      }
      return `${item.operator === 'do not include any' ? 'doesn\'t belong to' : 'belongs to'} ${partner.label || partner.name} ${listLabel} **${item.value || '(please set)'}**`
    }

    if (item.$type === 'eventHistory') {
      const eventName = item.eventName || '(please set)'
      const eventLabel = partner.espFeatureSupport?.eventHistory?.label?.toLowerCase() || 'event'

      if (preview) {
        return `Has fired ${eventLabel}`
      }

      switch (item.operator) {
        case 'has_fired':
          return `has fired ${partner.label || partner.name} ${eventLabel} **${eventName}**`
        case 'has_not_fired':
          return `has not fired ${partner.label || partner.name} ${eventLabel} **${eventName}**`
        case 'gte':
          return `has fired ${partner.label || partner.name} ${eventLabel} **${eventName}** at least **${item.count || 0}** times`
        case 'lt':
          return `has fired ${partner.label || partner.name} ${eventLabel} **${eventName}** less than **${item.count || 0}** times`
        case 'equals':
          return `has fired ${partner.label || partner.name} ${eventLabel} **${eventName}** exactly **${item.count || 0}** times`
        default:
          return `${partner.label || partner.name} ${eventLabel} condition`
      }
    }

    if (item.$type === 'purchaseHistory') {
      const purchaseLabel = partner.espFeatureSupport?.purchaseHistory?.label?.toLowerCase() || 'purchase'

      if (preview) {
        return `Has made a ${purchaseLabel}`
      }

      // Handle "anything" operators (is set / is not set)
      if (item.operator === 'is set') {
        return `has purchased anything in ${partner.label || partner.name}`
      }
      if (item.operator === 'is not set') {
        return `has not purchased anything in ${partner.label || partner.name}`
      }

      // Handle specific product operators (include any / do not include any)
      const productIds = item.productIds || []
      const productList = productIds.length > 0 ? productIds.join(' / ') : '(please set)'

      switch (item.operator) {
        case 'include any':
          return `has purchased **${productList}** in ${partner.label || partner.name}`
        case 'do not include any':
          return `has not purchased **${productList}** in ${partner.label || partner.name}`
        default:
          return `${partner.label || partner.name} ${purchaseLabel} condition`
      }
    }

    if (item.$type === 'totalRevenue') {
      const value = item.value ?? '(please set)'

      if (preview) {
        return `Total revenue in ${partner.label || partner.name}`
      }

      const operatorLabels: { [key: string]: string } = {
        'gte': 'at least',
        'gt': 'greater than',
        'lte': 'at most',
        'lt': 'less than',
        'equals': 'exactly'
      }
      const operatorLabel = operatorLabels[item.operator] || item.operator

      return `total revenue in ${partner.label || partner.name} is ${operatorLabel} **$${value}**`
    }
  }

  if (item.$source === 'espplaceholder') {
    if (item.$type === 'isAnonymous') {
      return `visitor is anonymous`
    }

    if (item.$type === 'isSubscriber') {
      return `visitor exists in email platform`
    }
  }

  if (item.$source === 'referrer') {
    if (item.$type === 'domain') {
      return `referred by ${item.domain || item.value || '(please set)'}`
    }

    if (item.$type === 'direct') {
      return `direct traffic`
    }

    if (item.$type === 'referral') {
      return `referred by any site`
    }

    if (item.$type === 'custom') {
      return `referrer ${operatorAndValue(item.operator, item.custom_referrer || item.value)}`
    }
  }

  if (item.$source === 'segments') {
    const dimension = segmentGroups.find((sg: any) => sg.id === item.$type)

    if ((item.operator || '').includes('set')) {
      return `**${dimension?.name || 'Segment'}** ${item.operator === 'is set' ? 'matches any segment' : 'matches no segment'}`
    }

    if (dimension && item.segmentId) {
      const segmentNames = dimension.segments
        ?.filter((s: any) => item.segmentId.includes(s.id))
        ?.map((s: any) => stripHtml(s.name || ''))
      return `**${dimension.name}** ${
        item.operator === 'do not include any' ? "isn't" : 'is'
      } **${segmentNames?.join(' / ') || '(please set)'}**`
    }
  }

  if (item.$source === 'rightmessage') {
    if (item.$type === 'widget') {
      const widgetNames = widgets
        .filter((w: any) => item.value?.includes(w.id))
        .map((w: any) => w.name)
        .join(' / ')

      return `current widget ${operatorAndValue(item.operator, widgetNames)}`
    }

    if (item.$type === 'question') {
      if (item.operator === 'is set') {
        return 'has answered any questions this page view'
      }
      if (item.operator === 'is not set') {
        return "hasn't answered any questions this page view"
      }
      
      if (!item.questionId || item.questionId.length === 0) {
        return `${item.operator === 'do not include any' ? "hasn't answered" : 'has answered'} **(please set)** this page view`
      }
      
      const questionTexts = questions
        .filter((q: any) => item.questionId?.includes(q.id))
        .map((q: any) => `"${stripHtml(q.text || '')}"`)
        .join(' / ')

      let inclusion = `has answered **${questionTexts || '(please set)'}**`
      if (item.operator === 'do not include any') {
        inclusion = `hasn't answered **${questionTexts || '(please set)'}**`
      }

      return `${inclusion} this page view`
    }
  }

  if (item.$source === 'scores') {
    if (item.$type === 'score') {
      return `score **${item.score}** ${operatorAndValue(item.operator, item.value)}`
    }

    if (item.$type === 'variable') {
      return `variable **${item.variableName || '(please set)'}** ${operatorAndValue(item.operator, item.value)}`
    }
  }

  if (item.$source === 'location') {
    if (item.$type === 'country') {
      return `country ${operatorAndValue(item.operator, item.value)}`
    }

    if (item.$type === 'city') {
      return `city ${operatorAndValue(item.operator, item.value)}`
    }
  }

  if (item.$source === 'pageviews') {
    if (item.$type === 'landingpage') {
      return `landing page ${operatorAndValue(item.operator, item.value)}`
    }

    if (item.$type === 'path') {
      if (item.occurrence === 'last') {
        return `current page ${operatorAndValue(item.operator, item.value)}`
      }
      
      if (item.occurrence === 'penultimate') {
        return `previous page ${operatorAndValue(item.operator, item.value)}`
      }

      const pagePhrase = item.operator === 'equals'
        ? item.value || '(please set)'
        : `page that ${operatorAndValue(item.operator, item.value)}`

      // Handle flexible occurrence format
      if (item.occurrence === 'any') {
        let visitDescription = ''
        
        // Build occurrence description
        if (item.occurrenceOperator === 'equals') {
          visitDescription = `visited ${pagePhrase} exactly ${item.occurrenceCount || 1} time${(item.occurrenceCount || 1) !== 1 ? 's' : ''}`
        } else if (item.occurrenceOperator === 'gte') {
          visitDescription = `visited ${pagePhrase} at least ${item.occurrenceCount || 1} time${(item.occurrenceCount || 1) !== 1 ? 's' : ''}`
        } else if (item.occurrenceOperator === 'lte') {
          visitDescription = `visited ${pagePhrase} at most ${item.occurrenceCount || 1} time${(item.occurrenceCount || 1) !== 1 ? 's' : ''}`
        } else if (item.occurrenceOperator === 'gt') {
          visitDescription = `visited ${pagePhrase} more than ${item.occurrenceCount || 1} time${(item.occurrenceCount || 1) !== 1 ? 's' : ''}`
        } else if (item.occurrenceOperator === 'lt') {
          visitDescription = `visited ${pagePhrase} less than ${item.occurrenceCount || 1} time${(item.occurrenceCount || 1) !== 1 ? 's' : ''}`
        } else {
          // Default to "at least" if operator not specified
          visitDescription = `visited ${pagePhrase}`
        }

        // Add timeframe
        if (item.timeframe === 'within') {
          visitDescription += ` in the last ${item.duration || 1} ${formatDuration(item.duration || 1, item.unit || 'day')}`
        } else if (item.timeframe === 'outside') {
          visitDescription += ` more than ${item.duration || 1} ${formatDuration(item.duration || 1, item.unit || 'day')} ago`
        }
        // For 'ever', no additional text needed

        return visitDescription
      }
      
      // Handle "has not visited" format
      if (item.occurrence === 'not_any') {
        let notVisitedDescription = `has not visited ${pagePhrase}`
        
        // Add timeframe
        if (item.timeframe === 'within') {
          notVisitedDescription += ` in the last ${item.duration || 1} ${formatDuration(item.duration || 1, item.unit || 'day')}`
        }
        // For 'ever', it means "has never visited"
        if (item.timeframe === 'ever') {
          notVisitedDescription = `has never visited ${pagePhrase}`
        }
        
        return notVisitedDescription
      }

      // Legacy format handling (for backward compatibility)
      const matches = item.occurrence?.match(/^any( (\d+))?$/)
      if (matches) {
        const num = matches[2] ? parseInt(matches[2]) : 1
        const numPhrase = num === 2 ? ' twice' : num === 3 ? ' three times' : ''
        return `visited ${pagePhrase}${numPhrase}${
          item.timeframe === 'this 7 days' ? ' in the last 7 days' : ''
        }`
      }

      return `visited ${pagePhrase}`
    }

    if (item.$type === 'newvisitor') {
      return 'first-time visitor'
    }

    if (item.$type === 'returningvisitor') {
      if (item.timeframe === 'ever') {
        return 'has visited before'
      } else if (item.timeframe === 'within') {
        return `has visited in the last ${item.duration} ${formatDuration(item.duration, item.unit)}`
      } else if (item.timeframe === 'outside') {
        return `last visit was more than ${item.duration} ${formatDuration(item.duration, item.unit)} ago`
      }
    }

    if (item.$type === 'pageCount') {
      const operatorLabel = comparisonLabels[item.occurrenceOperator] || 'at least'
      const count = item.occurrenceCount || 1
      let description = `viewed ${operatorLabel} **${count}** page${count !== 1 ? 's' : ''}`

      if (item.scope === 'filtered' && item.value) {
        description += ` matching **${item.value}**`
      }

      if (item.timeframe === 'within') {
        description += ` in the last ${item.duration || 1} ${formatDuration(item.duration || 1, item.unit || 'day')}`
      } else if (item.timeframe === 'outside') {
        description += ` more than ${item.duration || 1} ${formatDuration(item.duration || 1, item.unit || 'day')} ago`
      }

      return description
    }

    if (item.$type === 'sessionCount') {
      const operatorLabel = comparisonLabels[item.occurrenceOperator] || 'at least'
      const count = item.occurrenceCount || 1
      return `has visited ${operatorLabel} **${count}** session${count !== 1 ? 's' : ''}`
    }

    if (item.$type === 'visitorAge') {
      const operatorLabel = comparisonLabels[item.occurrenceOperator] || 'at least'
      const duration = item.duration || 1
      const unit = formatDuration(duration, item.unit || 'day')
      return `first visited ${operatorLabel} **${duration}** ${unit} ago`
    }
  }

  if (item.$source === 'date') {
    if (item.$type === 'date') {
      if (item.operator === 'between') {
        const startDate = item.value && !isNaN(new Date(item.value).getTime())
          ? new Date(item.value).toLocaleDateString()
          : '(please set)'
        const endDate = item.dateEnd && !isNaN(new Date(item.dateEnd).getTime())
          ? new Date(item.dateEnd).toLocaleDateString()
          : '(please set)'
        return `date ${operatorAndValue(item.operator, `${startDate} and ${endDate}`)}`
      } else {
        const startDate = item.value && !isNaN(new Date(item.value).getTime())
          ? new Date(item.value).toLocaleDateString()
          : '(please set)'
        return `date ${operatorAndValue(item.operator, startDate)}`
      }
    }

    if (item.$type === 'weekday') {
      const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      if (!item.value || (Array.isArray(item.value) && item.value.length === 0)) {
        return `day of the week ${operatorAndValue(item.operator, '(please set)')}`
      }
      const weekdays = Array.isArray(item.value) 
        ? item.value.map((v: any) => weekdayNames[parseInt(v)] || v).join(' / ')
        : item.value
      return `day of the week ${operatorAndValue(item.operator, weekdays)}`
    }

    if (item.$type === 'month') {
      if (!item.value || (Array.isArray(item.value) && item.value.length === 0)) {
        return `month ${operatorAndValue(item.operator, '(please set)')}`
      }
      const months = Array.isArray(item.value)
        ? item.value.map((v: any) => new Date(v).toLocaleDateString('en-US', { month: 'long' })).join(' / ')
        : item.value
      return `month ${operatorAndValue(item.operator, months)}`
    }
  }

  if (item.$source === 'utm') {
    return `${item.$type} ${operatorAndValue(item.operator, item.value)}`
  }

  if (item.$source === 'query') {
    if (item.$type === 'custom') {
      return `query parameter **${item.query || '(please set)'}** ${operatorAndValue(item.operator, item.value)}`
    }
  }

  if (item.$source === 'device') {
    if (item.$type === 'os') {
      if (item.operator === 'is set') {
        return 'operating system is detected'
      }
      if (item.operator === 'is not set') {
        return 'operating system is not detected'
      }
      const osValues = item.value && item.value.length > 0
        ? item.value.join(' / ')
        : '(please set)'
      return `operating system ${operatorAndValue(item.operator, osValues)}`
    }

    if (item.$type === 'browser') {
      if (item.operator === 'is set') {
        return 'browser is detected'
      }
      if (item.operator === 'is not set') {
        return 'browser is not detected'
      }
      const browserValues = item.value && item.value.length > 0
        ? item.value.join(' / ')
        : '(please set)'
      return `browser ${operatorAndValue(item.operator, browserValues)}`
    }
  }

  if (item.$source === 'jsdata') {
    if (item.$type === 'windowVariable') {
      return `javascript **window.${item.variable || '(please set)'}** ${operatorAndValue(item.operator, item.value)}`
    }
    if (item.$type === 'cookie') {
      return `cookie **${item.variable || '(please set)'}** ${operatorAndValue(item.operator, item.value)}`
    }
    if (item.$type === 'localStorage') {
      return `localStorage key **${item.variable || '(please set)'}** ${operatorAndValue(item.operator, item.value)}`
    }
  }

  return 'Condition configured'
}

/**
 * Describes a logic object by creating a human-readable sentence.
 */
export const describeLogic = async (
  logic: LogicObject,
  context: {
    preview?: boolean
    partners?: any[]
    segmentGroups?: any[]
    questions?: any[]
    widgets?: any[]
  } = {}
): Promise<string> => {
  if (!logic || !logic.or || logic.or.length === 0) {
    return 'No condition specified'
  }

  try {
    const orGroups = await Promise.all(
      logic.or.map(async (group) => {
        // Determine if this group uses AND or OR logic
        const items = group.and || group.or
        if (!items || items.length === 0) {
          return ''
        }
        const joiner = group.and ? ' and ' : ' or '

        const conditions = await Promise.all(
          items.map(async (item: LogicItem) => {
            return await describeLogicObject(item, context)
          })
        )

        return conditions.join(joiner)
      })
    )

    const filteredOrGroups = orGroups.filter((group) => group !== '')

    if (filteredOrGroups.length === 0) {
      return 'No condition specified'
    }

    return capitalize(filteredOrGroups.join(', or '))
  } catch (error) {
    console.error('Error in describeLogic:', error)
    return 'Complex condition (view details)'
  }
}